import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Bell, CheckCheck, Eye, CheckCircle2, FileSignature, FileDown, Receipt, CreditCard, CalendarClock, Folder } from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = { Eye, CheckCircle2, FileSignature, FileDown, Receipt, CreditCard, CalendarClock, Folder, Bell };

const REFRESH_MS = 20_000;

export default function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(false);
    const [panelStyle, setPanelStyle] = useState({});
    const navigate = useNavigate();
    const boxRef = useRef(null);
    const btnRef = useRef(null);

    const loadCount = useCallback(async () => {
        try {
            const r = await api.get('/notifications/unread-count');
            setUnread(r.data.unread_count || 0);
        } catch { /* ignore auth hiccups */ }
    }, []);

    const loadItems = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/notifications', { params: { limit: 20 } });
            setItems(r.data.items || []);
            setUnread(r.data.unread_count || 0);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        loadCount();
        const t = setInterval(loadCount, REFRESH_MS);
        return () => clearInterval(t);
    }, [loadCount]);

    useEffect(() => {
        if (!open) return;
        loadItems();
        // Position panel below-right of bell (fixed to escape overflow-hidden containers)
        if (btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            const panelW = 380;
            const vw = window.innerWidth;
            // Prefer to align the panel's left edge near the bell, spilling right.
            // If not enough room on the right, clamp to viewport.
            let left = r.left;
            if (left + panelW + 16 > vw) left = Math.max(8, vw - panelW - 16);
            setPanelStyle({ position: 'fixed', top: r.bottom + 8, left });
        }
        const onClick = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open, loadItems]);

    const markRead = async (id) => {
        try { await api.patch(`/notifications/${id}/read`); setItems(xs => xs.map(x => x.id === id ? { ...x, read: true } : x)); setUnread(u => Math.max(0, u - 1)); } catch { /* ignore */ }
    };
    const markAllRead = async () => {
        try {
            const r = await api.post('/notifications/mark-all-read');
            setItems(xs => xs.map(x => ({ ...x, read: true })));
            setUnread(0);
            if (r.data?.updated) toast.success(`Marked ${r.data.updated} as read`);
        } catch { /* ignore */ }
    };
    const openItem = (n) => {
        if (!n.read) markRead(n.id);
        if (n.link) { setOpen(false); navigate(n.link); }
    };

    return (
        <div className="relative" ref={boxRef} data-testid="notification-bell-root">
            <button
                ref={btnRef}
                onClick={() => setOpen(v => !v)}
                className="relative h-9 w-9 rounded-full bg-white border border-[color:var(--bm-border)] flex items-center justify-center text-[color:var(--bm-text-muted)] hover:text-[color:var(--bm-primary)] hover:border-[color:var(--bm-primary)] transition shadow-sm"
                aria-label="Notifications"
                data-testid="notification-bell-btn"
            >
                <Bell size={17} />
                {unread > 0 && (
                    <span data-testid="notification-unread-badge"
                          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--bm-accent-600)] text-white text-[10px] font-bold flex items-center justify-center">
                        {unread > 99 ? '99+' : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    style={panelStyle}
                    className="w-[380px] max-h-[520px] overflow-hidden rounded-xl bg-white border border-[color:var(--bm-border)] shadow-xl z-[999] flex flex-col"
                    data-testid="notification-panel"
                >
                    <div className="px-4 py-3 border-b border-[color:var(--bm-border)] flex items-center justify-between">
                        <div>
                            <div className="font-display text-[14px] font-semibold">Notifications</div>
                            <div className="text-[11px] text-[color:var(--bm-text-subtle)]">{unread} unread</div>
                        </div>
                        {unread > 0 && (
                            <button onClick={markAllRead} data-testid="notification-mark-all-btn"
                                    className="text-[11.5px] font-medium text-[color:var(--bm-primary)] hover:underline inline-flex items-center gap-1">
                                <CheckCheck size={13} /> Mark all read
                            </button>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {loading && <div className="px-4 py-6 text-center text-[12.5px] text-[color:var(--bm-text-muted)]">Loading…</div>}
                        {!loading && items.length === 0 && (
                            <div className="px-4 py-10 text-center">
                                <Bell size={24} className="mx-auto text-[color:var(--bm-text-subtle)]" />
                                <div className="mt-2 text-[12.5px] text-[color:var(--bm-text-muted)]">You're all caught up.</div>
                            </div>
                        )}
                        {!loading && items.map(n => {
                            const Icon = ICON_MAP[n.icon] || Bell;
                            return (
                                <button key={n.id} onClick={() => openItem(n)}
                                        data-testid={`notification-item-${n.id}`}
                                        className={`w-full text-left px-4 py-3 border-b border-[color:var(--bm-border)] last:border-0 hover:bg-[color:var(--bm-bg-mute)] transition flex items-start gap-3 ${n.read ? 'bg-white' : 'bg-[color:var(--bm-accent-50)]/40'}`}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${n.read ? 'bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]' : 'bg-[color:var(--bm-accent-600)] text-white'}`}>
                                        <Icon size={15} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <div className={`text-[13px] ${n.read ? 'font-medium text-[color:var(--bm-text)]' : 'font-semibold text-[color:var(--bm-primary)]'} truncate`}>{n.title}</div>
                                            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--bm-accent-600)] shrink-0" />}
                                        </div>
                                        {n.message && <div className="text-[12px] text-[color:var(--bm-text-muted)] mt-0.5 line-clamp-2">{n.message}</div>}
                                        <div className="text-[10.5px] text-[color:var(--bm-text-subtle)] mt-1">{new Date(n.created_at).toLocaleString('en-GB')}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
